import { useMemo } from 'react';

import type { PlantDistanceInfo } from '@core/hooks/useProductSection';
import type { Address } from '@core/types/api/Address/Address';
import type { Plant } from '@core/types/api/Plant/Plant';
import { hasValue } from '@core/utils/equality';
import { getPlantDistanceInfo } from '@core/utils/projectHelpers';
import type { LookupInputOption } from '@web/components/LookupInput/LookupInputSharedComponents';
import { lookups } from '@web/utils/DomainHelpers';

import { usePlantDistances } from './usePlantDistances';

/**
 * This hook is used to get the plants options with distances.
 * It is used to display the plants options with distances in the lookup input.
 */
export const useGetPlantsOptionsWithDistances = ({
  currentAddress,
}: {
  currentAddress: Partial<Address> | undefined;
}): {
  options: LookupInputOption[];
  distances: Map<string, PlantDistanceInfo>;
  isLoading: boolean;
  isPlantListLoaded: boolean;
} => {
  const {
    plants: allPlants,
    distanceByPlantId,
    isLoading,
    isPlantListLoaded,
  } = usePlantDistances({ address: currentAddress });

  const options = useMemo((): LookupInputOption[] => {
    if (allPlants.length === 0) {
      return [];
    }

    return lookups<Plant>({
      items: allPlants,
      count: allPlants.length,
      label: (p) => p.name,
      compare: (a, b) => {
        const aDistance = distanceByPlantId.get(a.id)?.distance?.meters || Infinity;
        const bDistance = distanceByPlantId.get(b.id)?.distance?.meters || Infinity;

        if (aDistance === bDistance) {
          return a.name.localeCompare(b.name);
        }
        return aDistance - bDistance;
      },
      sublabels: (p) => {
        if (!distanceByPlantId.has(p.id)) {
          if (p.address.city) {
            return [p.address.city];
          }
          if (p.address.state) {
            return [p.address.state];
          }
          return [];
        }

        const distanceResult = distanceByPlantId.get(p.id);

        const distanceInfo = hasValue(distanceResult)
          ? getPlantDistanceInfo({
              plantDriveTime: distanceResult.duration?.value.toISO() ?? null,
              plantDistance:
                distanceResult.distance?.toUnit(p.deliveryCosts.driveDistanceUnit)?.toString() ??
                null,
              plantDistanceUnit: p.deliveryCosts.driveDistanceUnit,
            })
          : null;

        const sublabels: string[] = [];
        if (p.address.city) {
          sublabels.push(p.address.city);
        } else if (p.address.state) {
          sublabels.push(p.address.state);
        }
        if (distanceInfo) {
          sublabels.push(
            distanceInfo.distanceText === distanceInfo.durationText
              ? distanceInfo.distanceText
              : `${distanceInfo.distanceText} | ${distanceInfo.durationText}`,
          );
        }
        return sublabels;
      },
    });
  }, [allPlants, distanceByPlantId]);

  const distances = useMemo(() => {
    const map = new Map<string, PlantDistanceInfo>();
    allPlants.forEach((plant) => {
      const distanceResult = distanceByPlantId.get(plant.id);
      if (distanceByPlantId.has(plant.id)) {
        map.set(plant.id, {
          plantDistance:
            distanceResult?.distance?.toUnit(plant.deliveryCosts.driveDistanceUnit)?.toString() ??
            null,
          plantDistanceUnit: plant.deliveryCosts.driveDistanceUnit,
          plantDriveTime: distanceResult?.duration?.value?.toISO() ?? null,
        });
      } else {
        map.set(plant.id, { plantDistance: null, plantDistanceUnit: null, plantDriveTime: null });
      }
    });
    return map;
  }, [allPlants, distanceByPlantId]);

  return { options, distances, isLoading, isPlantListLoaded };
};
